import { Box, Stack, Typography } from "@mui/material"
import { styled } from "@mui/material/styles"
import useEmblaCarousel from "embla-carousel-react"

import { Container, type FC, Image, Link, SvgIcon, Tag } from ".."
import data from "../../../mock_data/components/article-body-2-col.json"
import { ArticleBodySection } from "./styles"

type ArticleCardProps = {
  imageUrl: string
  title: string
  description: string
  link: { label: string; url: string }
}

const ArticleBody: FC = () => {
  const [emblaRef] = useEmblaCarousel({
    align: "start",
    containScroll: "trimSnaps",
    breakpoints: {
      "(min-width: 990px)": {
        active: false,
      },
    },
  })
  return (
    <Container
      maxWidth={1600}
      pt={{ xs: 20, lg: 0 }}
      pb={{ xs: 20, lg: 35 }}
      px={{ xs: 5, md: 10 }}
      display="flex"
      flexDirection={{ xs: "column", lg: "row" }}
      gap={{ xs: 5, lg: 25 }}
    >
      <Box maxWidth={1100} pt={{ xs: 0, lg: 35 }}>
        <ArticleBodySection dangerouslySetInnerHTML={{ __html: data.content }} />
        <ArticleShareLink />
      </Box>
      <ArticleAsideStyles px={{ xs: 0, lg: 20 }} pt={{ xs: 15, lg: 20 }}>
        <Box ref={emblaRef} mb={{ xs: 15, lg: 20 }} overflow="hidden">
          <Box
            display="grid"
            gridAutoFlow={{ xs: "column", lg: "row" }}
            rowGap={20}
            columnGap={3}
            gridAutoColumns={{ xs: "minmax(calc(100% - 100px), auto)", lg: "minmax(294px, auto)" }}
          >
            {data.items.map((card, i) => (
              <ArticleCard key={i} {...card} />
            ))}
          </Box>
        </Box>
        <ArticleRelatedDestinations />
      </ArticleAsideStyles>
    </Container>
  )
}

export default ArticleBody

/** A sub-component for the article share link */
const ArticleShareLink: FC = () => (
  <Stack direction="row" columnGap={3} alignItems="center" my={10}>
    <SvgIcon variant="share" size={14} viewBox="0 0 14 14" />
    <Link href={data.shareUrl} variant="link.xs" sx={{ background: "none", paddingBottom: 0 }}>
      Share this article
    </Link>
  </Stack>
)

/** A sub-component for a side elements */
const ArticleCard: FC<ArticleCardProps> = ({ imageUrl, title, description, link }) => (
  <Stack direction="column" minWidth={{ xs: 240, lg: "100%" }} mb={1}>
    <ArticleCardImage imageUrl={imageUrl} />
    <ArticleCardTitle title={title} />
    <ArticleCardDescription description={description} />
    <ArticleCardLink {...link} />
  </Stack>
)

/** A sub-component for the article card image */
const ArticleCardImage: FC<{ imageUrl: string }> = ({ imageUrl }) => (
  <Box mb={7}>
    <Image
      src={imageUrl}
      alt="card image"
      borderRadius={1.5}
      minWidth={{ xs: "auto", lg: 294 }}
      sx={{
        width: "100%",
      }}
    />
  </Box>
)

/** A sub-component for the article card title */
const ArticleCardTitle: FC<{ title: string }> = ({ title }) => (
  <Box mb={3}>
    <Typography variant="body.lg" component="h2" sx={(theme) => ({ color: theme.palette["neutral.700"].main })}>
      {title}
    </Typography>
  </Box>
)

/** A sub-component for the article card description */
const ArticleCardDescription: FC<{ description: string }> = ({ description }) => (
  <Box mb={5}>
    <Typography variant="body.sm" sx={(theme) => ({ color: theme.palette["neutral.700"].main })}>
      {description}
    </Typography>
  </Box>
)

/** A sub-component for the article card link */
const ArticleCardLink: FC<{ label: string; url: string }> = ({ label, url }) => (
  <Box>
    <Link
      href={url}
      variant="link.xs"
      sx={{
        color: (theme) => theme.palette["neutral.700"].main,
      }}
    >
      {label}
    </Link>
  </Box>
)

/** A subcomponent for the article related destinations section */
const ArticleRelatedDestinations: FC = () => (
  <Box pt={10} sx={{ borderTop: (theme) => `1px solid ${theme.palette["neutral.200"].main}` }}>
    <Typography variant="body.sm" component="div" mb={5}>
      Related Destinations
    </Typography>
    <Stack direction="row" alignItems="center" columnGap={2} mb={10}>
      {data.tags.map((tag, i) => (
        <Tag key={i} color="white" {...tag} hasBorder />
      ))}
    </Stack>
  </Box>
)

/** styled components  */

const ArticleAsideStyles = styled(Box)(
  ({ theme }) => `
    border-left: 1px solid ${theme.palette["neutral.200"].main};
    ${theme.breakpoints.down("lg")} {
      border-left: none;
      border-top: 1px solid ${theme.palette["neutral.200"].main};
    }
  `
)
